import { useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { DataTable } from "@/components/ui/data-table";
import { Trash2, X } from "lucide-react";
import { formatDate } from "@/utils/formatters";
import adminAffiliateRewardsService, {
  AffiliateRewardConfigurationRule,
} from "@/services/admin/affiliateRewardConfiguration.admin.service";

interface FormState {
  reward: string;
  rewardTypes: string[];
  earningThreshold: string;
  isActive: boolean;
}

export default function AffiliateRewardConfiguration() {
  const [rules, setRules] = useState<AffiliateRewardConfigurationRule[]>([]);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<AffiliateRewardConfigurationRule | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [form, setForm] = useState<FormState>({
    reward: "",
    rewardTypes: [],
    earningThreshold: "",
    isActive: true,
  });

  const [rewardTypeInput, setRewardTypeInput] = useState("");

  const handleAddRewardType = () => {
    const trimmedInput = rewardTypeInput.trim();
    if (trimmedInput && !form.rewardTypes.includes(trimmedInput)) {
      setForm((f) => ({
        ...f,
        rewardTypes: [...f.rewardTypes, trimmedInput],
      }));
      setRewardTypeInput("");
    }
  };

  const handleRemoveRewardType = (typeToRemove: string) => {
    setForm((f) => ({
      ...f,
      rewardTypes: f.rewardTypes.filter((type) => type !== typeToRemove),
    }));
  };

  const handleRewardTypeKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddRewardType();
    }
  };

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      const items = await adminAffiliateRewardsService.list();
      setRules(items);
      setIsLoading(false);
    };
    load();
  }, []);

  const resetForm = () => {
    setForm({
      reward: "",
      rewardTypes: [],
      earningThreshold: "",
      isActive: true,
    });
    setRewardTypeInput("");
  };

  const isFormValid =
    form.reward && form.earningThreshold && Number(form.earningThreshold) > 0;

  const handleCreate = async () => {
    if (!isFormValid) return;
    setSaving(true);
    try {
      const payload = {
        rewardName: form.reward,
        rewardTypes: form.rewardTypes,
        earningThreshold: Number(form.earningThreshold),
        isActive: form.isActive,
      };
      const created = await adminAffiliateRewardsService.create(payload);
      setRules((prev) => [created, ...prev]);
      resetForm();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await adminAffiliateRewardsService.remove(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
    } finally {
      setDeletingId(null);
    }
  };

  const [togglingId, setTogglingId] = useState<string | null>(null);

  const handleToggleActive = async (id: string, isActive: boolean) => {
    setTogglingId(id);
    try {
      const updated = await adminAffiliateRewardsService.update(id, {
        isActive,
      });
      setRules((prev) => prev.map((r) => (r.id === id ? updated : r)));
    } finally {
      setTogglingId(null);
    }
  };

  const columns = useMemo<ColumnDef<AffiliateRewardConfigurationRule>[]>(
    () => [
      {
        accessorKey: "rewardName",
        header: "Reward",
        meta: { width: "w-44" },
        cell: ({ row }) => (
          <span className="font-medium text-foreground">
            {row.original.rewardName}
          </span>
        ),
      },
      {
        id: "rewardTypes",
        header: "Reward types",
        meta: { width: "w-64" },
        cell: ({ row }) =>
          row.original.rewardTypes && row.original.rewardTypes.length > 0 ? (
            <div className="flex flex-wrap gap-1 py-1">
              {row.original.rewardTypes.map((type, index) => (
                <span
                  key={index}
                  className="bg-blue-100 dark:bg-blue-500/15 text-blue-800 dark:text-blue-300 px-2 py-0.5 rounded-md text-xs"
                >
                  {type}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: "earningThreshold",
        header: "Earning threshold",
        meta: { width: "w-40" },
        cell: ({ row }) => (
          <span className="text-foreground">
            ${row.original.earningThreshold.toLocaleString()}
          </span>
        ),
      },
      {
        accessorKey: "updatedAt",
        header: "Last updated",
        meta: { width: "w-36" },
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.updatedAt ? formatDate(row.original.updatedAt) : "—"}
          </span>
        ),
      },
      {
        id: "active",
        header: "Active",
        meta: { width: "w-40" },
        cell: ({ row }) => {
          const rule = row.original;
          return (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch
                checked={rule.isActive}
                onCheckedChange={(val: boolean) =>
                  handleToggleActive(rule.id, val)
                }
                disabled={togglingId === rule.id}
              />
              <span>
                {togglingId === rule.id ? (
                  <div className="flex items-center gap-1">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-muted-foreground"></div>
                    {rule.isActive ? "Deactivating..." : "Activating..."}
                  </div>
                ) : rule.isActive ? (
                  "Active"
                ) : (
                  "Inactive"
                )}
              </span>
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        meta: {
          width: "w-20",
          headerClassName: "text-right",
          cellClassName: "text-right",
        },
        cell: ({ row }) => {
          const rule = row.original;
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteTarget(rule)}
              disabled={deletingId === rule.id}
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10"
            >
              {deletingId === rule.id ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [togglingId, deletingId],
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Create Affiliate Reward Rules</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Input
                type="text"
                placeholder="Reward Name"
                value={form.reward}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((f) => ({
                    ...f,
                    reward: e.target.value,
                  }))
                }
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="Reward Type"
                  value={rewardTypeInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setRewardTypeInput(e.target.value)
                  }
                  onKeyDown={handleRewardTypeKeyDown}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddRewardType}
                  disabled={
                    !rewardTypeInput.trim() ||
                    form.rewardTypes.includes(rewardTypeInput.trim())
                  }
                  className="px-3"
                >
                  Add
                </Button>
              </div>
              {form.rewardTypes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {form.rewardTypes.map((type, index) => (
                    <div
                      key={index}
                      className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => handleRemoveRewardType(type)}
                        className="w-3 h-3 cursor-pointer"
                      />
                      <span>{type}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveRewardType(type)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Earning Amount"
                value={form.earningThreshold}
                onKeyDown={(e) => {
                  const allowed = [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                    "Home",
                    "End",
                  ];
                  if (allowed.includes(e.key)) return;
                  if (!/^[0-9]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => (e.currentTarget as HTMLInputElement).blur()}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setForm((f) => ({
                    ...f,
                    earningThreshold: e.target.value.replace(/[^0-9]/g, ""),
                  }))
                }
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-3 h-9">
              <Switch
                className="data-[state=checked]:bg-[#d7785b] data-[state=checked]:hover:bg-[#c66a4f] text-white"
                checked={form.isActive}
                onCheckedChange={(val: boolean) =>
                  setForm((f) => ({ ...f, isActive: val }))
                }
              />
              <span className="text-sm text-muted-foreground">Active</span>
            </div>
            <Button
              className="bg-[#d7785b] hover:bg-[#c66a4f] w-32 text-white"
              disabled={!isFormValid || saving}
              onClick={handleCreate}
            >
              {saving ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </div>
              ) : (
                "Create Reward"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>Rewards</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <DataTable
              columns={columns}
              data={rules}
              isLoading={isLoading}
              skeletonRows={4}
              getRowId={(row) => row.id}
              emptyText="No Rewards yet"
            />
          </CardContent>
        </Card>
      </div>

      <ConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            handleDelete(deleteTarget.id);
          }
          setDeleteTarget(null);
        }}
        title="Delete Reward"
        message="Are you sure you want to delete this reward? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteTarget ? deletingId === deleteTarget.id : false}
      />
    </div>
  );
}
